import { UpdateTicketContactDialog } from "@jugl-web/domain-resources/tickets/components/UpdateTicketContactDialog";
import { useUpdateTicketContactDialog } from "@jugl-web/domain-resources/tickets/hooks/useUpdateTicketContactDialog";
import {
  ClientContact,
  TicketDetailsModel,
  TicketListItemModel,
  UpdateTicketPayload,
} from "@jugl-web/rest-api";
import { NewContactDialog } from "@jugl-web/domain-resources/contacts/components/NewContactDialog";
import { Avatar, TableCell } from "@jugl-web/ui-kit";
import { useToast } from "@jugl-web/utils";
import { TicketContactPickerPopover } from "@web-src/modules/client-support/components/TicketContactPickerPopover/TicketContactPickerPopover";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useState } from "react";

interface ContactCellContentProps {
  ticket: TicketListItemModel;
  onUpdate: (
    data: UpdateTicketPayload
  ) => Promise<TicketDetailsModel | undefined>;
}

export const ContactCellContent: FC<ContactCellContentProps> = ({
  ticket,
  onUpdate,
}) => {
  const [isNewContactDialogOpen, setIsNewContactDialogOpen] = useState(false);

  const { entityId } = useEntitySelectedProvider();

  const { toast } = useToast();

  const updateTicketContact = useCallback(
    async (contactId: string) => {
      await onUpdate({ contact_id: contactId });
      toast("Ticket customer has been updated");
    },
    [onUpdate, toast]
  );

  const { updateTicketContactDialogProps, requestUpdateContact } =
    useUpdateTicketContactDialog({
      currentContact: ticket.contact,
      onContactUpdate: updateTicketContact,
    });

  const handleContactCreated = (createdContact: ClientContact) => {
    requestUpdateContact({
      id: createdContact.id,
      name: createdContact.name ?? "Unknown",
    });
  };

  return (
    <>
      <TicketContactPickerPopover
        contact={ticket.contact}
        onChange={requestUpdateContact}
        onNewContactSelect={() => setIsNewContactDialogOpen(true)}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => (
          <Trigger
            as={TableCell}
            ref={triggerRef}
            isFocused={isOpen}
            isHoverable
            classNames={{ content: "flex items-center gap-2 truncate" }}
          >
            <Avatar size="sm" imageUrl={null} username={ticket.contact.name} />
            <span className="truncate text-[13px] leading-[140%] font-normal text-dark">
              {ticket.contact.name}
            </span>
          </Trigger>
        )}
      />
      <UpdateTicketContactDialog {...updateTicketContactDialogProps} />
      <NewContactDialog
        entityId={entityId}
        isOpen={isNewContactDialogOpen}
        onRequestClose={() => setIsNewContactDialogOpen(false)}
        onContactCreated={handleContactCreated}
      />
    </>
  );
};
